import { BadgeCheck, Sparkles } from "lucide-react";
import { Reveal, PageHeader } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { useBooking } from "@/context/BookingContext";

export default function Doctors() {
  const { content } = useContent();
  const { book } = useBooking();
  const doctors = content?.doctors || [];

  return (
    <div className="px-4 pb-24 pt-44 sm:px-8 lg:px-16" data-testid="doctors-page">
      <PageHeader
        eyebrow="Our Physicians"
        title="Masters of Their Craft"
        subtitle="Board-certified dermatologists and aesthetic physicians with decades of combined expertise — and an obsession with natural results."
        testid="doctors-title"
      />

      <div className="mx-auto mt-16 max-w-6xl space-y-20" data-testid="doctors-list">
        {doctors.map((d, i) => (
          <Reveal key={d.id}>
            <div className="grid items-center gap-10 lg:grid-cols-2" data-testid={`doctor-profile-${d.id}`}>
              <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                <div className="relative">
                  <div aria-hidden="true" className="absolute -inset-3 rounded-[2.5rem] bg-gradient-to-br from-[#D4AF37]/20 to-transparent" />
                  <img src={d.image} alt={`Portrait of ${d.name}`} loading="lazy" className="relative aspect-[4/5] w-full rounded-[2.5rem] object-cover shadow-[0_20px_60px_rgba(26,22,21,0.15)]" />
                </div>
              </div>
              <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                <p className="eyebrow">{d.role}</p>
                <h2 className="mt-3 font-serif text-3xl font-semibold text-[#1A1615] sm:text-4xl">{d.name}</h2>
                <p className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37]/10 px-4 py-1.5 text-xs font-medium text-[#8a6d1f]">
                  <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> {d.experience} of clinical excellence
                </p>
                <p className="mt-5 text-base font-light leading-relaxed text-[#5A504B]">{d.bio}</p>
                <div className="mt-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1A1615]">Specializations</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(d.specializations || []).map((sp) => (
                      <span key={sp} className="glass rounded-full px-4 py-1.5 text-xs font-medium text-[#5A504B]">
                        {sp}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mt-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1A1615]">Certifications</p>
                  <ul className="mt-3 space-y-2">
                    {(d.certifications || []).map((c) => (
                      <li key={c} className="flex items-center gap-2 text-sm font-light text-[#5A504B]">
                        <BadgeCheck className="h-4 w-4 shrink-0 text-[#B38B2D]" aria-hidden="true" /> {c}
                      </li>
                    ))}
                  </ul>
                </div>
                <button onClick={() => book()} data-testid={`doctor-book-${d.id}`} className="btn-gold mt-8 px-7 py-3.5 text-sm">
                  Book with {d.name.split(" ")[1] ? `Dr. ${d.name.split(" ").slice(-1)[0]}` : d.name}
                </button>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
